import type { CSSProperties } from 'react'
import {
  APP_COVER_FRAMES,
  type AppCoverFrame,
  coverInFrame,
} from '~/features/shared/lib/cover-crop'
import * as m from '~/paraglide/messages'

const FRAMES: { frame: AppCoverFrame; label: () => string }[] = [
  { frame: 'feed', label: m.coverAppPreviewFeed },
  { frame: 'event', label: m.coverAppPreviewEvent },
  { frame: 'chat', label: m.coverAppPreviewChat },
]

/**
 * Shows a picture the way the app does. `picture` is placed inside a cover-format box; without
 * `crop` the whole file fills that box from the middle, as an uncropped cover does.
 */
export function CoverAppPreview({
  src,
  placement,
  title,
}: {
  src: string
  /** How the dialog's current section sits in the cover-format box, in percent. */
  placement?: CSSProperties
  title?: string
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs">{title ?? m.coverAppPreviewTitle()}</p>
      <div className="grid grid-cols-3 items-end gap-2">
        {FRAMES.map(({ frame, label }) => {
          const box = coverInFrame(frame)
          const { width, height } = APP_COVER_FRAMES[frame]
          return (
            <div key={frame} className="min-w-0 space-y-1">
              <div
                className="squircle relative overflow-hidden rounded-lg bg-muted"
                style={{ aspectRatio: width / height }}
              >
                <div
                  className="absolute overflow-hidden"
                  style={{
                    width: `${box.width}%`,
                    height: `${box.height}%`,
                    left: `${box.left}%`,
                    top: `${box.top}%`,
                  }}
                >
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    className={
                      placement
                        ? 'pointer-events-none absolute max-w-none'
                        : 'h-full w-full object-cover'
                    }
                    style={placement}
                  />
                </div>
                {frame === 'feed' ? (
                  <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-black/70 to-transparent"
                  />
                ) : null}
              </div>
              <p className="truncate text-muted-foreground text-xs">{label()}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
